import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { leafletLayer } from "protomaps-leaflet";
import L from "leaflet";
import { FetchSource, PMTiles, TileType } from "pmtiles";

const getHeaderBounds = (header) => {
  const minLat = Number(header?.minLat);
  const minLon = Number(header?.minLon);
  const maxLat = Number(header?.maxLat);
  const maxLon = Number(header?.maxLon);

  if (
    !Number.isFinite(minLat) ||
    !Number.isFinite(minLon) ||
    !Number.isFinite(maxLat) ||
    !Number.isFinite(maxLon)
  ) {
    return null;
  }

  return L.latLngBounds([minLat, minLon], [maxLat, maxLon]);
};

const getRasterMimeType = (tileType) => {
  switch (tileType) {
    case TileType.Png:
      return "image/png";
    case TileType.Jpeg:
      return "image/jpeg";
    case TileType.Webp:
      return "image/webp";
    case TileType.Avif:
      return "image/avif";
    default:
      return "";
  }
};

const createRasterLayer = (source, header, options) => {
  const mimeType = getRasterMimeType(header.tileType);

  const PmtilesRasterGridLayer = L.GridLayer.extend({
    createTile(coords, done) {
      const controller = new AbortController();
      const tile = document.createElement("img");
      let objectUrl = null;
      let isCancelled = false;
      const cleanupObjectUrl = () => {
        if (objectUrl) {
          window.URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      };

      tile.alt = "";
      tile.setAttribute("role", "presentation");
      tile.cancel = () => {
        isCancelled = true;
        controller.abort();
        cleanupObjectUrl();
      };

      source
        .getZxy(coords.z, coords.x, coords.y, controller.signal)
        .then((tileResponse) => {
          if (!tileResponse) {
            tile.cancel = undefined;
            done(undefined, tile);
            return;
          }

          if (isCancelled) {
            tile.cancel = undefined;
            done(undefined, tile);
            return;
          }

          const blob = new Blob([tileResponse.data], { type: mimeType });
          objectUrl = window.URL.createObjectURL(blob);

          tile.onload = () => {
            cleanupObjectUrl();
            tile.cancel = undefined;
            done(undefined, tile);
          };
          tile.onerror = () => {
            cleanupObjectUrl();
            tile.cancel = undefined;
            done(new Error("Impossibile decodificare il tile PMTiles"), tile);
          };
          tile.src = objectUrl;
        })
        .catch((error) => {
          tile.cancel = undefined;

          if (error?.name === "AbortError") {
            done(undefined, tile);
            return;
          }

          done(error, tile);
        });

      return tile;
    },

    _removeTile(key) {
      const tile = this._tiles[key];

      if (!tile) {
        return;
      }

      tile.el.cancel?.();
      L.GridLayer.prototype._removeTile.call(this, key);
    },
  });

  return new PmtilesRasterGridLayer(options);
};

const PmtilesLayer = ({
  url,
  zIndex = 350,
  customHeaders,
  fitToBounds = false,
  fitMaxZoom = 18,
  onBoundsChange,
}) => {
  const map = useMap();
  const layerRef = useRef(null);

  useEffect(() => {
    if (!url) {
      return undefined;
    }

    let isDisposed = false;

    const paneName = "pmtiles-overlay-pane";
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
    }
    pane.style.zIndex = String(zIndex);
    pane.style.pointerEvents = "none";

    const setupLayer = async () => {
      try {
        const pmtilesSource = customHeaders
          ? new FetchSource(url, customHeaders)
          : url;
        const source = new PMTiles(pmtilesSource);
        const header = await source.getHeader();

        if (isDisposed) {
          return;
        }

        const layerBounds = getHeaderBounds(header);
        onBoundsChange?.(layerBounds);
        const layer =
          header.tileType === TileType.Mvt
            ? leafletLayer({ url: source, pane: paneName })
            : createRasterLayer(source, header, {
                pane: paneName,
                opacity: 1,
                minNativeZoom: header.minZoom,
                maxNativeZoom: header.maxZoom,
                maxZoom: header.maxZoom,
                ...(layerBounds ? { bounds: layerBounds } : {}),
              });

        if (typeof layer?.setZIndex === "function") {
          layer.setZIndex(zIndex);
        }

        layer.addTo(map);
        if (typeof layer?.bringToFront === "function") {
          layer.bringToFront();
        }

        if (fitToBounds && layerBounds) {
          map.fitBounds(layerBounds, {
            maxZoom: fitMaxZoom,
            padding: [16, 16],
          });
        }
        layerRef.current = layer;
      } catch {}
    };

    setupLayer();

    return () => {
      isDisposed = true;
      onBoundsChange?.(null);
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map, url, zIndex, customHeaders, fitToBounds, fitMaxZoom, onBoundsChange]);

  return null;
};

export default PmtilesLayer;
